import React, { FC, useState, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useAlert } from 'react-alert'
import { URL } from '../../constants/url'
import { useDispatch, useSelector } from 'react-redux'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import '../../assets/scss/tutorial.scss'

import { setPath } from '../../../utils/url'
import { RootState } from '../../../states/containers/store'
import { createIntroPage } from '../../../states/containers/common/introPagesSlice'
import { getPageId, hasRead } from '../../../utils/introPage'
import { getMemberExistence } from '../../../states/apis/memberExistence'
import { getDesktopAppInstallerLink } from '../../../states/apis/desktopApp'
import { setErrors } from '../../../states/containers/common/errorsSlice'
import { password } from '../../../utils/validate'
import { fetchProfile } from '../../../states/containers/setting/profileSlice'
import { useTranslation } from 'react-i18next'
import { updatePassword } from '../../../states/containers/tutorial/managerInfoSlice'
import { InstallerInfo } from '../../../states/types/desktopApp'
import Modal from '../../components/common/Modal'
import WorkStyleDetail from '../../components/common/WorkStyleDetail'

type LangValue = 'ja' | 'en'

const MemberTutorial: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()
  const { t } = useTranslation()
  const WELCOME_PAGE = 1
  const QASEE_DESCRIPTION_PAGE_1 = 2
  const QASEE_DESCRIPTION_PAGE_2 = 3
  const AUTH_CHECK_PAGE = 4
  const CHANGE_PASSWORD_PAGE = 5
  const CLIENT_INSTALL_CHECK_PAGE = 6
  const PROFILE_PAGE = 7
  const OAUTH_DESCRIPTION_PAGE = 8
  const TUTORIAL_END_PAGE = 9

  const initialIntro = {
    show: false,
    currentPage: 1,
  }
  const [intro, setIntro] = useState(initialIntro)
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [windowsInfo, setWindowsInfo] = useState<InstallerInfo>()
  const [macosInfo, setMacosInfo] = useState<InstallerInfo>()

  const [passwordErrors, setPasswordErrors] = useState<string[] | undefined>()
  const [confirmationPasswordError, setConfirmationPasswordError] = useState<string>("")
  const [isPasswordSubmit, setIsPasswordSubmit] = useState<boolean>(false)
  const [isPasswordLoading, setIsPasswordLoading] = useState<boolean>(false)
  const [isPasswordSuccess, setIsPasswordSuccess] = useState<boolean>(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmationPasswordRef = useRef<HTMLInputElement>(null)

  /* eslint-disable max-len */
  const ManualLink = "https://qasee.zendesk.com/hc/ja/articles/49548559717657"
  /* eslint-enable max-len */
  const {
    tosAndPrivacyPolicy,
    profile,
    login,
  } = useSelector((state: RootState) => state)
  const {
    read_first_intro_ids,
  } = useSelector((state: RootState) => (state.commonPart))

  const clientLoggedInPolling = () => {
    getMemberExistence()
      .then((result) => {
        if (result.exists) {
          setIsLoggedIn(true)
        } else {
          setTimeout(clientLoggedInPolling, 5000)
        }
      })
  }

  useEffect(() => {
    if (tosAndPrivacyPolicy.agreed === undefined) return
    const memberPageId = Number(getPageId(URL.TUTORIAL_MEMBER.path))
    const tutorialFinished = Boolean(read_first_intro_ids?.includes(memberPageId))
    setTermsAgreed(tosAndPrivacyPolicy.agreed && !tutorialFinished)
  }, [tosAndPrivacyPolicy, read_first_intro_ids])

  useEffect(() => {
    if (intro.currentPage === CLIENT_INSTALL_CHECK_PAGE && !isLoggedIn) {
      clientLoggedInPolling()
    }
  }, [intro.currentPage])

  useEffect(() => {
    if (!login.accessToken) return
    getDesktopAppInstallerLink()
      .then((result) => {
        if (language === 'ja') {
          setWindowsInfo(result?.windows)
          setMacosInfo(result?.macos)
        } else {
          setWindowsInfo(result?.windows_en)
          setMacosInfo(result?.macos_en)
        }
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
    dispatch(fetchProfile())
  }, [login.accessToken])

  const showPasswordResetModal = () => {
    setIntro({
      ...intro,
      currentPage: CLIENT_INSTALL_CHECK_PAGE,
    })
  }

  useEffect(() => {
    if (read_first_intro_ids && (hasRead(URL.TUTORIAL_MASTER.path, read_first_intro_ids)
        || hasRead(URL.TUTORIAL_MEMBER.path, read_first_intro_ids))) {
      if (login.function_enabled_state?.ai_work_assessment) {
        history.push(setPath(URL.REPORT.path, language))
        return
      }
      if (login.function_enabled_state?.task_management) {
        history.push(setPath(URL.TASK.path, language))
        return
      }
      history.push("/")
      return
    }

    if (login.feature_config?.password_reset === undefined) return
    if (login.feature_config?.password_reset) {
      showPasswordResetModal()
    }
  }, [])

  const handleNextIntro = () => {
    if (intro.currentPage === CLIENT_INSTALL_CHECK_PAGE && !isLoggedIn) {
      alert.error("次へ進むには、QaseeClientからログインしてください")
      return
    }
    if (!login.function_enabled_state?.ai_work_assessment && intro.currentPage === CLIENT_INSTALL_CHECK_PAGE) {
      setIntro({
        ...intro,
        currentPage: intro.currentPage + 2,
      })
      return
    }
    setIntro({
      ...intro,
      currentPage: intro.currentPage + 1,
    })
  }

  const handleBackIntro = () => {
    if (!login.function_enabled_state?.ai_work_assessment && intro.currentPage === OAUTH_DESCRIPTION_PAGE) {
      setIntro({
        ...intro,
        currentPage: intro.currentPage - 2,
      })
      return
    }
    setIntro({
      ...intro,
      currentPage: intro.currentPage - 1,
    })
  }

  const finishIntro = () => {
    dispatch(
      createIntroPage(
        URL.TUTORIAL_MEMBER.path,
        () => {
          setIntro(initialIntro)
          let url = URL.TOP.path
          if (login.function_enabled_state?.ai_work_assessment) {
            url = URL.REPORT.path
          } else if (login.function_enabled_state?.task_management) {
            url = URL.TASK.path
          }
          history.push(setPath(url, language))
        },
      ),
    )
  }

  const renderStepBar = (nextEnable = true) => {
    const visibility = intro.currentPage === WELCOME_PAGE || intro.currentPage === CLIENT_INSTALL_CHECK_PAGE ? "hidden" : "visible"
    let buttonText = "次へ"
    let onClickAction = handleNextIntro
    if (intro.currentPage === TUTORIAL_END_PAGE) {
      buttonText = "完了"
      onClickAction = finishIntro
    }
    return (
      <>
        <button
          type="button"
          className="btn btn-default pull-left"
          onClick={handleBackIntro}
          style={{ visibility: visibility }}
        >
          戻る
        </button>
        <span>
          <span className="bootstrapWizard form-wizard">
            <span id="step-1" className={(intro.currentPage >= WELCOME_PAGE ? 'active' : '')}><span className="step" /></span>
            <span id="step-2" className={(intro.currentPage >= QASEE_DESCRIPTION_PAGE_1 ? 'active' : '')}><span className="step" /></span>
            <span id="step-3" className={(intro.currentPage >= QASEE_DESCRIPTION_PAGE_2 ? 'active' : '')}><span className="step" /></span>
            <span id="step-4" className={(intro.currentPage >= AUTH_CHECK_PAGE ? 'active' : '')}><span className="step" /></span>
            <span id="step-5" className={(intro.currentPage >= CHANGE_PASSWORD_PAGE ? 'active' : '')}><span className="step" /></span>
            <span id="step-6" className={(intro.currentPage >= CLIENT_INSTALL_CHECK_PAGE ? 'active' : '')}><span className="step" /></span>
            {
              login.function_enabled_state?.ai_work_assessment && (
                <span id="step-7" className={(intro.currentPage >= PROFILE_PAGE ? 'active' : '')}><span className="step" /></span>
              )
            }
            <span id="step-8" className={(intro.currentPage >= OAUTH_DESCRIPTION_PAGE ? 'active' : '')}><span className="step" /></span>
            <span id="step-9" className={(intro.currentPage >= TUTORIAL_END_PAGE ? 'active' : '')}><span className="step" /></span>
          </span>
        </span>
        <button
          type="button"
          className="btn btn-primary pull-right next-button"
          disabled={!nextEnable}
          onClick={onClickAction}
        >
          {buttonText}
        </button>
      </>
    )
  }

  const matchPassword = (value: string) => {
    if (!value) {
      setConfirmationPasswordError("")
      setIsPasswordSubmit(false)
      return
    }
    const passwordString = passwordRef.current?.value
    if (passwordString === value) {
      setConfirmationPasswordError("")
      if (passwordErrors) {
        setIsPasswordSubmit(false)
      } else {
        setIsPasswordSubmit(true)
      }
    } else {
      setConfirmationPasswordError("パスワードが一致しません")
      setIsPasswordSubmit(false)
    }
  }

  const checkPassword = (value: string) => {
    const confirmationPassword = confirmationPasswordRef.current?.value
    if (confirmationPassword) {
      matchPassword(confirmationPassword)
    }
    if (!value) {
      setPasswordErrors(undefined)
      setIsPasswordSubmit(false)
      return
    }
    const errors = password(value, { email: profile.email })
    if (errors) {
      setPasswordErrors(password(value, { email: profile.email }))
      setIsPasswordSubmit(false)
    } else {
      setPasswordErrors(undefined)
    }
  }

  const handlePasswordSubmit = () => {
    if (!isPasswordSubmit) return
    setIsPasswordLoading(true)
    const params = { password: passwordRef.current?.value, password_confirmation: confirmationPasswordRef.current?.value }
    dispatch(updatePassword(
      params,
      () => {
        alert.success('初期パスワードの変更が完了しました。')
        setIsPasswordLoading(false)
        setIsPasswordSuccess(true)
      },
      () => {
        setIsPasswordLoading(false)
        setIsPasswordSuccess(false)
      },
    ))
  }

  const keyPress = (value: string) => {
    if (value === 'Enter') {
      handlePasswordSubmit()
    }
  }

  const renderTutorialModal1 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        へようこそ！
      </h1>
      <div className="modal-body">
        <div className="modal-description">
          <p>
            Qaseeは組織の透明性を高め強いチーム体制を確立できる
            <br />
            組織力の強化を目的としたシステムです
          </p>
          <img className="description-image" src="/static/images/tutorial/Qasee_manager_01.png" alt="description" />
        </div>
      </div>
    </>
  )

  const renderTutorialModal2 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        を導入することで、働くことをより楽しく前向きに
      </h1>
      <div className="modal-body">
        <div className="modal-description">
          <p>
            業務にまつわることを幅広くカバーします
          </p>
          <img className="description-image" src="/static/images/tutorial/Qasee_manager_02.png" alt="description" />
        </div>
      </div>
    </>
  )

  const renderTutorialModal3 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        でできること
      </h1>
      <div className="modal-body">
        <div className="modal-description" style={{ display: "flex", justifyContent: "center" }}>
          <div className="col-sm-6 modal-description-block" style={{ backgroundColor: "#6296D5", color: "#fff" }}>
            <p className="description-title">ユーザーにとってのQasee</p>
            <img className="description-image" src="/static/images/tutorial/Qasee_manager_03.png" alt="description" />
            <p className="description-title">作業効率を上げられます</p>
            <p className="description-sentence">
              働き方を変え、自由度を増やし、
              <br />
              圧倒的なパフォーマンスで業務を行うための
              <br />
              HR Tech SYSTEM
            </p>
          </div>
          <div className="col-sm-6 modal-description-block">
            <p className="description-title">マネージャーにとってのQasee</p>
            <img className="description-image" src="/static/images/tutorial/Qasee_manager_04.png" alt="description" />
            <p className="description-title">マネジメント業務から開放されます</p>
            <p className="description-sentence">
              組織の健全化とマネジメントコストを減らす
              <br />
              強固なチームづくりを行うための
              <br />
              HR Tech SYSTEM
            </p>
          </div>
        </div>
      </div>
    </>
  )

  const renderTutorialModal4 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        あなたの権限
      </h1>
      <div className="modal-body">
        <div className="modal-description" style={{ width: "60%", display: "block" }}>
          <p style={{ fontSize: "24px", textAlign: "left" }}>
            あなたの権限は…
          </p>
          <img className="description-image" src="/static/images/tutorial/Qasee_manager_03.png" alt="description" />
          <div style={{ textAlign: "center" }}>
            <span className="occupation">ユーザー(使用者)</span>
            <span>です</span>
          </div>
        </div>
      </div>
    </>
  )

  const renderTutorialModal5 = () => (
    <>
      <h1 className="modal-title text-center password">
        <span className="blue">初期パスワード</span>
        を変更しましょう
      </h1>
      <div className="modal-body password">
        <div className="modal-description">
          アカウントのセキュリティを強化するため、初期パスワードの変更をお願い致します。
        </div>
        <div className="field">
          <div className="input-area">
            <p>新しいパスワード</p>
            <input
              type="password"
              placeholder="半角英数字8~64文字"
              onChange={(e) => checkPassword(e.target.value)}
              className={passwordErrors && "error"}
              ref={passwordRef}
              disabled={isPasswordLoading || isPasswordSuccess}
            />
            {
              passwordErrors && passwordErrors.map((error: string) => (
                <React.Fragment key={error}>
                  <div className="password_error">{t(`${error}`)}</div>
                </React.Fragment>
              ))
            }
          </div>
          <div className="input-area">
            <p>新しいパスワード(確認)</p>
            <input
              type="password"
              placeholder="半角英数字8~64文字"
              onChange={(e) => matchPassword(e.target.value)}
              onKeyPress={(e) => keyPress(e.key)}
              className={confirmationPasswordError && "error"}
              ref={confirmationPasswordRef}
              disabled={isPasswordLoading || isPasswordSuccess}
            />
            {
              confirmationPasswordError && (
                <div className="password_error">{confirmationPasswordError}</div>
              )
            }
          </div>
          <div className="button-area">
            {
              isPasswordSuccess ? (
                <button type="button" className="btn btn-primary" disabled>
                  適用済み
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!isPasswordSubmit || isPasswordLoading}
                  onClick={() => handlePasswordSubmit()}
                >
                  {
                    isPasswordLoading ? (
                      <div className="loading" />
                    ) : (
                      '適用する'
                    )
                  }
                </button>
              )
            }
          </div>
          <div className="supplement">
            ※以下の指定した文字以外の入力禁止
            <br />
            ①英字(大文字、小文字) ②数字 ③記号(! &#34; $ &#39; ( ) * + , - . / ; &lt; = &gt; ? @ ¥ &#123; &#124; &#125; ~)
            <br />
            ※8文字以上64文字以下 ※パスワードにメールアドレスの単語が含まれていないか
            <br />
            ※英字が2種類以上(大文字小文字指定なし)、数字が2種類以上使われているか
            <br />
            ※同じ文字を3回連続で使っていないか
          </div>
        </div>
      </div>
    </>
  )

  const renderTutorialModal6 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        をインストールして、分析を開始しましょう
      </h1>
      <div className="modal-body">
        <div className="modal-description" style={{ textAlign: "left", width: "96%" }}>
          <p className="install">QaseeClientアプリのインストール</p>
          <p className="install-description1">
            Qaseeを使って自身の分析結果を得るには、QaseeClientアプリのインストールが必要です。
            <br />
            以下のリンクからインストーラーのダウンロード、インストール、ログインをお願いいたします。
          </p>
          <div className="install-description2">
            <p style={{ fontWeight: "bold" }}>
              ※QaseeClientアプリからのログイン後、次の画面へ進めます。
              <br />
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <table className="step-table">
              <tr>
                <td className="step">Step1</td>
                <td className="step-description">QaseeClientのダウンロード</td>
                <td>
                  <a
                    className="btn btn-primary download-button-win"
                    href={windowsInfo?.direct_download_url}
                  >
                    <img className="download-icon" src="/static/images/tutorial/download.svg" alt="download-icon" />
                    {`for Windows (v${windowsInfo?.latest_version})`}
                  </a>
                </td>
                <td>
                  <a
                    className="btn btn-primary download-button-mac"
                    href={macosInfo?.direct_download_url}
                  >
                    <img className="download-icon" src="/static/images/tutorial/download.svg" alt="download-icon" />
                    {`for macOS (v${macosInfo?.latest_version})`}
                  </a>
                </td>
              </tr>
              <tr>
                <td className="step">Step2</td>
                <td className="step-description">QaseeClientのインストール</td>
                <td colSpan={2}>
                  <a
                    className="manual-link"
                    href={ManualLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img className="manual-icon" src="/static/images/tutorial/book-opened.svg" alt="manual-icon" />
                    マニュアル
                  </a>
                </td>
              </tr>
              <tr>
                <td className="step">Step3</td>
                <td className="step-description">QaseeClientからのログイン</td>
                <td style={{ textAlign: "right" }}>
                  <span className="status">ステータス:</span>
                </td>
                <td style={{ textAlign: "left" }}>
                  <div className="status-block">
                    <span
                      className={isLoggedIn ? "logged-in" : "not-logged-in"}
                    >
                      {isLoggedIn ? (
                        <>ログイン確認済み</>
                      ) : (
                        <>未ログイン</>
                      )}
                    </span>
                    {isLoggedIn ? (
                      <div className="check-mark" />
                    ) : (
                      <div className="disabled-check-mark" />
                    )}
                  </div>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </>
  )

  const renderTutorialModal7 = () => (
    <div
      style={{
        margin: "32px 0 16px",
      }}
    >
      <WorkStyleDetail
        closeCallback={() => { /**/ }}
        type="tutorial"
      />
    </div>
  )

  const renderTutorialModal8 = () => (
    <>
      <h1 className="modal-title text-center sso-connect">
        <span className="blue">外部ID</span>
        でかんたんログイン
      </h1>
      <div className="modal-body sso-connect">
        <div className="modal-description">
          <div>
            <img src="/static/images/tutorial/image-sso-connect.png" alt="設定で外部ID連携すると簡単にログインできるようになりました。" />
          </div>
          <div className="text-area">
            <div className="detail-title">
              設定で外部ID連携すると簡単にログインできるようになりました。
            </div>
            <div>
              お使いのGoogle・Microsoft 365アカウントと連携することで、 カレンダーの予定をQaseeに同期することができます。
              <br />
              連携後にはメールアドレス・パスワードを用いたログインに加えて、連携したアカウントでのQaseeへのログインも可能になります。
              <br />
              今後はログイン画面にて、「Googleアカウントでログイン」もしくは「Microsoft 365アカウントでログイン」ボタンをクリックしてログインいただけます。
            </div>
          </div>
        </div>
      </div>
    </>
  )

  const renderTutorialModal9 = () => (
    <>
      <h1 className="modal-title text-center">
        <img className="qasee-logo" src="/static/images/tutorial/Qasee.png" alt="qasee-logo" />
        <span className="occupation">をさっそく、使ってみましょう！</span>
      </h1>
      <div className="modal-body" style={{ backgroundImage: 'url("/static/images/tutorial/Qasee_manager_05.png")' }}>
        <div className="modal-description" style={{ width: "100%" }}>
          <p style={{ fontSize: "16px", lineHeight: "24px" }}>
            Qaseeは組織の透明性を高め強いチーム体制を確立できる
            <br />
            組織力の強化を目的としたシステムです
          </p>
        </div>
      </div>
    </>
  )
  return (
    <>
      <Modal
        show={termsAgreed}
        modalSize="large"
        wrapperClass="tutorial"
      >
        <>
          { intro.currentPage === WELCOME_PAGE && renderTutorialModal1() }
          { intro.currentPage === QASEE_DESCRIPTION_PAGE_1 && renderTutorialModal2() }
          { intro.currentPage === QASEE_DESCRIPTION_PAGE_2 && renderTutorialModal3() }
          { intro.currentPage === AUTH_CHECK_PAGE && renderTutorialModal4() }
          { intro.currentPage === CHANGE_PASSWORD_PAGE && renderTutorialModal5() }
          { intro.currentPage === CLIENT_INSTALL_CHECK_PAGE && renderTutorialModal6() }
          { intro.currentPage === PROFILE_PAGE && renderTutorialModal7() }
          { intro.currentPage === OAUTH_DESCRIPTION_PAGE && renderTutorialModal8() }
          { intro.currentPage === TUTORIAL_END_PAGE && renderTutorialModal9() }
        </>
        {renderStepBar(
          !((intro.currentPage === CHANGE_PASSWORD_PAGE && !isPasswordSuccess)
          || (intro.currentPage === PROFILE_PAGE && !(profile.occupation_id && profile.work_style_ids.length))),
        )}
      </Modal>
    </>
  )
}

export default MemberTutorial
